import { createRoot, hydrateRoot } from "react-dom/client";
// Latin-only Inter subset: the site copy is English, so we drop the cyrillic,
// greek, vietnamese, and latin-ext @font-face blocks the default imports pull
// in (7 subsets → 1). Smaller payload, faster first paint. "Speed is the brand."
import "@fontsource/inter/latin-400.css";
import "@fontsource/inter/latin-500.css";
import "@fontsource/inter/latin-600.css";
import "@fontsource/inter/latin-700.css";
import App from "./App.client";
import { initAnalytics } from "@/lib/analytics";
import "./index.css";

declare global {
  interface Window {
    // Set by prerender.mjs in each prerendered file; the route that file's
    // markup was rendered for. Absent in dev (no prerender).
    __PRERENDER_PATH__?: string;
  }
}

// Before the first render, so a track() call from the first paint's components
// is queued rather than dropped. It is a no-op with no key, in dev, or when the
// visitor has set Do Not Track / Global Privacy Control — and posthog-js is a
// dynamic import, so in those cases the chunk is never even fetched.
//
// It reads the stored consent choice (localStorage `capnolog_consent`) FIRST and
// initializes on the tier that choice selects, so a returning visitor who
// accepted is recording from the first frame and one who declined — or has not
// answered — gets the cookieless tier with no replay and nothing loaded from
// PostHog's host. The bar that asks is src/components/layout/ConsentBar.tsx,
// mounted by App.client.tsx.
initAnalytics();

const container = document.getElementById("root")!;
const norm = (p: string) => p.replace(/\/+$/, "") || "/";
const prerenderedPath = window.__PRERENDER_PATH__;

// Hydrate only when this file was actually prerendered for the current URL.
// If a SPA-fallback host served a different route's file (e.g. home for an
// unmatched path), or in dev, do a clean client render instead — this avoids a
// hydration mismatch and matches the pre-prerender behavior exactly.
if (
  prerenderedPath !== undefined &&
  container.hasChildNodes() &&
  norm(prerenderedPath) === norm(window.location.pathname)
) {
  hydrateRoot(container, <App />);
} else {
  container.innerHTML = "";
  createRoot(container).render(<App />);
}
